import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Building2, ImageUp, Pencil } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
  tenantFormSchema,
  tenantKeys,
  tenantQueryOptions,
  updateTenant,
  type TenantFormValues,
} from '@/features/tenant/api'
import { ApiError } from '@/shared/api/api-error'
import type { TenantOut } from '@/shared/api/types'
import { formatDocument } from '@/shared/lib/document'
import { useApiErrorMessage } from '@/shared/i18n/use-api-error-message'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Card, CardContent } from '@/shared/ui/card'
import { Drawer } from '@/shared/ui/drawer'
import { EmptyState } from '@/shared/ui/empty-state'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { Skeleton } from '@/shared/ui/skeleton'
import { PageHeader } from '@/widgets/app-shell/page-header'

export const Route = createFileRoute('/_authed/configuracoes/empresa')({
  component: EmpresaPage,
})

function EmpresaPage() {
  const { t } = useTranslation(['company', 'nav', 'common'])
  const { data, isLoading, error, refetch } = useQuery(tenantQueryOptions())

  return (
    <>
      <PageHeader title={t('nav:settings.company')} />
      <div className="max-w-4xl p-6">
        {isLoading ? (
          <Card>
            <CardContent className="flex flex-col gap-4 pt-4">
              <Skeleton className="h-10 w-64" />
              <Skeleton className="h-24 w-full" />
            </CardContent>
          </Card>
        ) : error ? (
          // 404 = tenant não provisionado (orienta); demais = falha de carga com retry.
          error instanceof ApiError && error.status === 404 ? (
            <EmptyState
              icon={Building2}
              title={t('company:notProvisioned.title')}
              description={t('company:notProvisioned.description')}
            />
          ) : (
            <EmptyState
              icon={Building2}
              title={t('company:loadError')}
              action={
                <Button variant="secondary" onClick={() => void refetch()}>
                  {t('common:actions.retry')}
                </Button>
              }
            />
          )
        ) : data ? (
          <EmpresaView tenant={data} />
        ) : null}
      </div>
    </>
  )
}

/**
 * Vista da empresa no formato do print (Nibo): nome + lápis de edição, bloco de
 * dados cadastrais e o placeholder de logo à direita. Só `name` e `document`
 * vêm do backend (`GET /v1/tenants/me`); Razão Social exibe o próprio `name`
 * (é o único nome que o modelo tem) e Endereço não existe no backend → "Em breve".
 */
function EmpresaView({ tenant }: { tenant: TenantOut }) {
  const { t } = useTranslation(['company', 'common'])
  const [editando, setEditando] = useState(false)
  const comingSoon = t('common:comingSoon.title')

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start gap-3">
        <h2 className="text-ink text-xl font-semibold">{tenant.name}</h2>
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          aria-label={t('company:editName')}
          onClick={() => {
            setEditando(true)
          }}
        >
          <Pencil className="size-4" aria-hidden />
        </Button>
      </div>

      <div className="grid gap-8 sm:grid-cols-[1fr_auto]">
        <dl className="flex flex-col gap-5">
          <div className="flex flex-col gap-0.5">
            <dt className="text-ink-muted text-xs font-medium">{t('company:details.cnpj')}</dt>
            <dd className="text-ink text-sm">
              {tenant.document !== null && tenant.document !== ''
                ? formatDocument(tenant.document)
                : t('company:details.notInformed')}
            </dd>
          </div>

          <div className="flex flex-col gap-0.5">
            <dt className="text-ink-muted text-xs font-medium">{t('company:details.legalName')}</dt>
            <dd className="text-ink text-sm">{tenant.name}</dd>
          </div>

          {/* Endereço não existe no TenantOut do backend → placeholder honesto. */}
          <div className="flex flex-col gap-0.5">
            <dt className="text-ink-muted flex items-center gap-2 text-xs font-medium">
              {t('company:details.address')}
              <Badge tone="soon">{t('common:comingSoon.badge')}</Badge>
            </dt>
            <dd className="text-ink-subtle text-sm">{t('company:details.notInformed')}</dd>
          </div>
        </dl>

        {/* Upload de logo: sem endpoint de upload → placeholder desabilitado. */}
        <div className="flex flex-col items-center gap-2">
          <div
            className="border-line text-ink-subtle flex size-[100px] flex-col items-center justify-center gap-1 rounded-md border border-dashed text-center"
            aria-disabled
            title={comingSoon}
          >
            <ImageUp className="size-6" aria-hidden />
            <span className="text-2xs px-1 leading-tight">{t('company:logo.upload')}</span>
          </div>
          <Badge tone="soon">{t('common:comingSoon.badge')}</Badge>
          <p className="text-ink-subtle text-2xs max-w-[100px] text-center leading-tight">
            {t('company:logo.hint')}
          </p>
        </div>
      </div>

      <hr className="border-line" />

      {/* Importar/Exportar/Limpar/Excluir: sem endpoints no backend → "Em breve". */}
      <ul className="flex flex-col gap-3">
        {(['import', 'export', 'clear', 'delete'] as const).map((acao) => (
          <li key={acao}>
            <span
              className="text-ink-subtle inline-flex cursor-not-allowed items-center gap-2 text-sm"
              aria-disabled
              title={comingSoon}
            >
              {t(`company:dataActions.${acao}`)}
              <Badge tone="soon">{t('common:comingSoon.badge')}</Badge>
            </span>
          </li>
        ))}
      </ul>

      <EmpresaEditDrawer tenant={tenant} open={editando} onOpenChange={setEditando} />
    </div>
  )
}

/**
 * Drawer de edição — reusa o schema/mutation do tenant. É a ÚNICA parte
 * funcional (real) da tela: `PATCH /v1/tenants/me` com `name` + `document`.
 */
function EmpresaEditDrawer({
  tenant,
  open,
  onOpenChange,
}: {
  tenant: TenantOut
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation(['company', 'common', 'validation'])
  const qc = useQueryClient()
  const mensagemDeErro = useApiErrorMessage()

  const form = useForm<TenantFormValues>({
    resolver: zodResolver(tenantFormSchema),
    defaultValues: { name: tenant.name, document: tenant.document ?? '' },
  })

  const salvar = useMutation({
    mutationFn: updateTenant,
    onSuccess: async (salvo) => {
      await qc.invalidateQueries({ queryKey: tenantKeys.me() })
      toast.success(t('company:saved'))
      form.reset({ name: salvo.name, document: salvo.document ?? '' })
      onOpenChange(false)
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.isValidation) {
        for (const [campo, msg] of Object.entries(err.fieldErrors())) {
          form.setError(campo as keyof TenantFormValues, { message: msg })
        }
        return
      }
      // 403 (sem finance_admin) e 409 (CNPJ em uso): mensagem pelo código.
      toast.error(mensagemDeErro(err))
    },
  })

  const onSubmit = form.handleSubmit((v) => {
    salvar.mutate(v)
  })

  // Só traduz o que tem prefixo de namespace conhecido (as do Zod); mensagem
  // pronta do backend passa direto. (Mesmo cuidado dos form-drawers.)
  const NS = /^(validation|common):/
  const erro = (campo: keyof TenantFormValues): string | undefined => {
    const m = form.formState.errors[campo]?.message
    if (m === undefined) return undefined
    return NS.test(m) ? t(m as 'validation:required') : m
  }

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={t('company:editName')}
      footer={
        <>
          <Button variant="primary" onClick={() => void onSubmit()} disabled={salvar.isPending}>
            {salvar.isPending ? t('common:states.loading') : t('common:actions.save')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              onOpenChange(false)
            }}
          >
            {t('common:actions.cancel')}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void onSubmit()
        }}
        className="flex flex-col gap-4"
        noValidate
      >
        <div className="flex flex-col gap-1">
          <Label htmlFor="name">
            {t('company:fields.name')}
            <span className="text-danger ml-0.5">*</span>
          </Label>
          <Input id="name" {...form.register('name')} aria-invalid={erro('name') !== undefined} />
          {erro('name') !== undefined && (
            <span role="alert" className="text-danger text-2xs">
              {erro('name')}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <Label htmlFor="document">{t('company:fields.document')}</Label>
          <Input
            id="document"
            inputMode="numeric"
            placeholder={t('company:fields.documentPlaceholder')}
            {...form.register('document')}
            aria-invalid={erro('document') !== undefined}
          />
          {erro('document') !== undefined && (
            <span role="alert" className="text-danger text-2xs">
              {erro('document')}
            </span>
          )}
        </div>
      </form>
    </Drawer>
  )
}
